'use memo';

import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiChatInput } from '@/components/AiChatInput/AiChatInput';
import { Card, CardContent, CardHeader } from '@/components/Card/card';
import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import RootHeader from '@/components/Layout/RootHeader';
import { ViewportLoader } from '@/components/Loading/ViewportLoader';
import { ProgressiveBlur } from '@/components/ProgressiveBlur';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ChatInterfaceRightAutomap } from '@/features/automap-ingester';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/cn';
import type { AiMockSide } from '@/mocks/db/schema';
import { CockpitMockSidebar } from '@/routes/_cockpit-mock/cockpit-mock/-components/CockpitMockSidebar';
import GlobalHeaderPc from '@/routes/_individual/individual/-components/GlobalHeader/GlobalHeaderPc';
import GlobalHeaderSp from '@/routes/_individual/individual/-components/GlobalHeader/GlobalHeaderSp';
import { IndividualSidebar } from '@/routes/_individual/individual/-components/IndividualSidebar';
import { AnswerRenderer } from './-components/answers/AnswerRenderer';
import { AIActionCard } from './-components/chat/AIActionCard';
import { ChatHistory } from './-components/chat/ChatHistory';
import { ChatInterfaceFloatingCard } from './-components/ChatInterfaceFloatingCard';
import { ChatInterfaceFooter } from './-components/ChatInterfaceFooter';
import { ChatInterfaceRightCards } from './-components/ChatInterfaceRightCards';
import { ChatInterfaceRightDetail } from './-components/ChatInterfaceRightDetail';
import { buildEffectiveHistory, useSequenceRunner } from './-hooks/useSequenceRunner';
import { useStagedReveal } from './-hooks/useStagedReveal';
import { useCompleteSequence, useRecordSequenceAnswer, useSequenceSidebarData, useSetRightPanelMode, useSetSidebarOpen, useUploadSequenceFiles } from './-query';
import type { SequenceRunnerView } from './-types';
export const Route = createFileRoute('/_chat/chat/$sessionId/')({
  component: SequenceRunnerRoute
});
function SequenceRunnerRoute() {
  const {
    sessionId
  } = Route.useParams();
  const sequenceInstanceId = sessionId;
  const result = useSequenceRunner(sequenceInstanceId);
  if (result.status === 'loading') {
    return <ViewportLoader />;
  }
  if (result.status === 'error') {
    return <NotFoundCard />;
  }
  return <SequenceRunnerReady view={result.view} sequenceInstanceId={sequenceInstanceId} />;
}
type SequenceRunnerReadyProps = {
  view: SequenceRunnerView;
  sequenceInstanceId: string;
};
function SequenceRunnerReady({
  view,
  sequenceInstanceId
}: SequenceRunnerReadyProps) {
  const recordAnswer = useRecordSequenceAnswer(sequenceInstanceId);
  const uploadFiles = useUploadSequenceFiles(sequenceInstanceId);
  const setRightPanelMode = useSetRightPanelMode(sequenceInstanceId);
  const setSidebarOpen = useSetSidebarOpen(sequenceInstanceId);
  const completeSequence = useCompleteSequence(sequenceInstanceId);
  const side = view.definition.side ?? 'individual';
  const sidebarData = useSequenceSidebarData(side);
  const {
    isMobile
  } = useDesktopOrMobileMediaQuery();

  // Per-step reveal cache: skip the staged animation if the user has already
  // seen this step in the current page-visit (e.g. they navigated back via
  // the sidebar). State (rather than a ref) so it can be read safely during
  // render — the extra commit on completion is intentional and one-shot.
  const [revealedStepKeys, setRevealedStepKeys] = useState<Set<string>>(() => new Set());

  // detail で開く対象 content（シフト編成の「店舗のシフト表」/「シフト希望」など）。
  // rightPanelMode と違い永続化しない一時 UI 状態。reference クリックで設定される。
  const [draftTargetKey, setDraftTargetKey] = useState<string | null>(null);
  const {
    instance,
    definition,
    currentStep,
    currentStepRecord,
    currentStepIndex
  } = view;
  const {
    staticItems,
    stagedItems
  } = useMemo(() => buildEffectiveHistory(view), [view]);

  // status を含めることで、申請完了で status が "completed" に切り替わったタイミングに
  // useStagedReveal の resetKey が変わり、completionHistory が staged reveal される。
  const stepKey = `${instance.id}:${currentStep.id}:${instance.status}`;
  const alreadyShown = revealedStepKeys.has(stepKey);
  const handleRevealComplete = useCallback(() => {
    setRevealedStepKeys(prev => {
      if (prev.has(stepKey)) return prev;
      const next = new Set(prev);
      next.add(stepKey);
      return next;
    });
  }, [stepKey]);
  const reveal = useStagedReveal(stagedItems, stepKey, {
    alreadyShown,
    onComplete: handleRevealComplete
  });

  // tool_call アイテムが reveal された瞬間（thinking 完了直後）に automap パネルを自動展開する。
  // hasAutoOpenedAutomap で「初回のみ」発火を保証し、ユーザーが「修正する」で
  // rightPanelMode を "cards" に戻した後に再発火して上書きしてしまうバグを防ぐ。
  const hasAutoOpenedAutomap = useRef(false);
  // ステップが切り替わったらフラグをリセットする。
  useEffect(() => {
    hasAutoOpenedAutomap.current = false;
  }, [currentStep.id]);
  const toolCallRevealed = reveal.visibleItems.some(item => item.kind === 'tool_call');
  useEffect(() => {
    if (!hasAutoOpenedAutomap.current && toolCallRevealed && currentStep.answer.kind === 'automap_review' && instance.rightPanelMode !== 'automap') {
      hasAutoOpenedAutomap.current = true;
      setRightPanelMode.mutate({
        instanceId: instance.id,
        mode: 'automap'
      });
    }
  }, [toolCallRevealed, currentStep.answer.kind, instance.rightPanelMode, instance.id, setRightPanelMode]);
  const isAutomap = currentStep.answer.kind === 'automap_review' && instance.rightPanelMode === 'automap';
  const isDetail = !isAutomap && instance.rightPanelMode === 'detail';
  const isWide = isDetail || isAutomap;
  const isLastStep = currentStepIndex === definition.steps.length - 1;
  const isCompleted = instance.status === 'completed';
  // 完了後も Detail を開ける状態にしておくため、完了後の最終 step でも dock は出す。
  const showSubmitDock = isDetail && (isLastStep || isCompleted);
  // `complete` CTA を押した後、または確定済みの場合に true。
  // この時点でドラフトの導線（chat link / レファレンス entry）を表示する。
  const hasDraft = isCompleted || currentStep.answer.kind === 'complete' && currentStepRecord.answer?.kind === 'complete';
  const openDraftDetail = (targetKey?: string) => {
    setDraftTargetKey(targetKey ?? null);
    setRightPanelMode.mutate({
      instanceId: instance.id,
      mode: 'detail'
    });
  };
  const openAutomapPanel = () => {
    setRightPanelMode.mutate({
      instanceId: instance.id,
      mode: 'automap'
    });
  };
  const automapUploadedFiles = (() => {
    if (!isAutomap || currentStep.answer.kind !== 'automap_review') return [];
    const uploadStepId = currentStep.answer.uploadStepId;
    const record = view.stepRecords.find(r => r.stepId === uploadStepId);
    if (!record || record.answer?.kind !== 'upload') return [];
    return record.answer.files;
  })();
  const Sidebar = side === 'cockpit' ? CockpitMockSidebar : IndividualSidebar;
  const handleSidebarToggle = () => {
    if (isWide) {
      // Detail / Automap モード中はサイドバートグルで cards モードに戻し、
      // サイドバーを展開する。Automap は currentStep に紐づくので
      // 実際には next step に進まないと閉じきらないが、UX 上の一貫性を担保。
      setRightPanelMode.mutate({
        instanceId: instance.id,
        mode: 'cards'
      });
      setSidebarOpen.mutate({
        instanceId: instance.id,
        open: true
      });
      return;
    }
    setSidebarOpen.mutate({
      instanceId: instance.id,
      open: !instance.sidebarOpen
    });
  };
  const closeRightPanel = () => setRightPanelMode.mutate({
    instanceId: instance.id,
    mode: 'cards'
  });

  // detail / automap の中身。PC では右ペインにインライン表示、モバイルでは
  // ボトムシートに表示する（cards モードのときは null）。
  const wideRightPanel = isAutomap && currentStep.answer.kind === 'automap_review' ? <ChatInterfaceRightAutomap title={currentStep.answer.title} subtitle={currentStep.answer.subtitle} certificates={currentStep.answer.certificates} uploadedFiles={automapUploadedFiles} confirmLabel={currentStep.answer.confirmLabel} isSubmitting={recordAnswer.isPending} onSubmit={answer => {
    recordAnswer.mutate({
      instanceId: instance.id,
      stepId: currentStep.id,
      answer
    });
  }} onClose={closeRightPanel} /> : isDetail ? <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <ChatInterfaceRightDetail draft={definition.detailDraft} confirmed={isCompleted} currentStepIndex={Math.max(currentStepIndex, 0)} isLastStep={isLastStep} detailTargetKey={draftTargetKey} onSubmitAnswer={answer => recordAnswer.mutate({
      instanceId: instance.id,
      stepId: currentStep.id,
      answer
    })} onClose={closeRightPanel} />
        {showSubmitDock ? <div className={cn('relative z-30 shrink-0 border-l border-t border-neutral-muted-border bg-neutral-fill px-7 pt-4 pb-6', 'shadow-[0_-10px_36px_-14px_rgba(15,23,42,0.1)]')}>
            <ChatInterfaceFooter isSubmitting={completeSequence.isPending} submitLabel={definition.submitLabel} completed={isCompleted} onEdit={closeRightPanel} onSubmit={() => {
        completeSequence.mutate(sequenceInstanceId);
      }} />
          </div> : null}
      </div> : null;
  return <VStack className={cn('h-full', side === 'cockpit' ? '' : 'bg-blue-50')}>
      <PageHeader side={side} />
      <Separator />
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-neutral-bottom-fill">
        <div className="flex min-h-0 flex-1">
          {!isMobile && <Sidebar mode="chat" open={!isWide && instance.sidebarOpen} onToggle={handleSidebarToggle} recentSessions={sidebarData.data?.recentSessions ?? []} upcomingInteractions={sidebarData.data?.upcomingInteractions ?? []} />}
          <main className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
            <section className={cn('relative flex min-h-0 min-w-0 flex-col bg-neutral-bottom-fill transition-[flex] duration-300', isWide && !isMobile ? 'flex-[0_0_400px]' : 'flex-1')}>
              <ProgressiveBlur className="right-3 h-9 to-neutral-bottom-fill" />
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <ChatHistory staticItems={staticItems} stagedItems={reveal.visibleItems} pendingNextItem={stagedItems[reveal.visibleItems.length]} isThinking={reveal.isThinking} scrollAnchorKey={stepKey} onOpenDraft={openDraftDetail} onOpenAutomap={openAutomapPanel} />
                  <ProgressiveBlur position="bottom" blurAmount={8} className="pointer-events-none right-3 z-10 h-9 to-neutral-bottom-fill" />
                </div>
                <div className="relative z-20 shrink-0 bg-neutral-bottom-fill px-4 pb-6">
                  {isWide || !reveal.isComplete || instance.status === 'completed' || currentStep.answer.kind === 'automap_review' ? <AiChatInput className="max-w-none" /> : <ChatInterfaceFloatingCard contentKey={stepKey} showKeyboardHints={currentStep.answer.kind === 'yes_no' || currentStep.answer.kind === 'single_choice' || currentStep.answer.kind === 'single_choice_with_specs' || currentStep.answer.kind === 'multi_choice' || currentStep.answer.kind === 'field_confirm'} header={<AIActionCard title={currentStep.title} subtitle={currentStep.subtitle} badge={deriveAiBadge(definition.packageTitle)} />} body={<AnswerRenderer answer={currentStep.answer} stepRecord={currentStepRecord} isSubmitting={recordAnswer.isPending} isUploading={uploadFiles.isPending} onSubmit={answer => {
                  recordAnswer.mutate({
                    instanceId: instance.id,
                    stepId: currentStep.id,
                    answer
                  });
                }} onUpload={files => {
                  uploadFiles.mutate({
                    instanceId: instance.id,
                    stepId: currentStep.id,
                    files
                  });
                }} onSkipUpload={() => {
                  recordAnswer.mutate({
                    instanceId: instance.id,
                    stepId: currentStep.id,
                    answer: {
                      kind: 'upload',
                      files: [],
                      skipped: true
                    }
                  });
                }} />} />}
                </div>
              </div>
            </section>
            {!isMobile && (isWide ? wideRightPanel : <ChatInterfaceRightCards definition={definition} currentStepIndex={Math.max(currentStepIndex, 0)} hasDraft={hasDraft} onOpenDraft={openDraftDetail} onOpenAutomap={openAutomapPanel} />)}
          </main>
          {isMobile && <Sheet open={isWide} onOpenChange={next => {
          if (!next) closeRightPanel();
        }}>
              <SheetContent side="bottom" className="flex h-[92vh] flex-col gap-0 p-0" hideCloseButton>
                <SheetHeader className="sr-only">
                  <SheetTitle>{currentStep.title}</SheetTitle>
                </SheetHeader>
                {wideRightPanel}
              </SheetContent>
            </Sheet>}
        </div>
      </div>
    </VStack>;
}

/** packageTitle に含まれる担当部署キーワードから AI バッジを派生する。 */
function deriveAiBadge(packageTitle: string): string {
  if (/情シス/.test(packageTitle)) return 'DRESS CODE AI: IT';
  if (/総務/.test(packageTitle)) return 'DRESS CODE AI: GA';
  if (/人事|労務|採用/.test(packageTitle)) return 'DRESS CODE AI: HR';
  return 'DRESS CODE AI';
}
function PageHeader({
  side
}: {
  side: AiMockSide;
}) {
  const {
    isDesktop
  } = useDesktopOrMobileMediaQuery();
  if (side === 'cockpit') return <RootHeader />;
  return isDesktop ? <GlobalHeaderPc /> : <GlobalHeaderSp />;
}
function NotFoundCard() {
  return <div className="flex h-full w-full items-center justify-center bg-neutral-bottom-fill">
      <Card className="max-w-md p-8">
        <CardHeader className="space-y-2 p-0">
          <Text as="h2" variant="h3">
            シーケンスが見つかりません
          </Text>
          <Text variant="pSmall" className="text-neutral-muted-on-fill">
            指定された手続きは見つからないか、利用できなくなりました。
          </Text>
        </CardHeader>
        <CardContent className="mt-6 p-0">
          <Link to="/individual/portal" className="inline-flex items-center gap-1 text-primary-on-neutral-fill hover:underline">
            <Icon iconName="ChevronLeft" size={14} colorVariant="primary" />
            <Text variant="label" className="text-primary-on-neutral-fill">
              ポータルに戻る
            </Text>
          </Link>
        </CardContent>
      </Card>
    </div>;
}